/**
 * The campaign card's decisions, where a runner can reach them — the reason
 * `campaigns.ts` gives for being a module rather than a component.
 *
 * The card (`components/CampaignCard.tsx`, 2026-10-21) shows everything this console
 * can read about one campaign on one screen: the campaign row, every line item under
 * it, each line item's targeting criteria and promoted tweets, and the text of each
 * promoted tweet. Five reads, all of them `GET`s already in `lib/api.ts`; this module
 * holds what is decided about them and nothing about rendering.
 *
 * 🔴 **Four things are decided here and not on the screen**, because each is a place a
 * tidy-up would break quietly:
 *
 * - the line-item list is paged (`LineItemPage.nextCursor`), so the card follows the
 *   cursor to the end — or, past `MAX_PAGES`, says how many it shows and that more were
 *   not read (`allPages`);
 * - the country list is asked with no `q` at all, not `q=`: the contract says nothing
 *   is defaulted on that route, so an empty `q=` would be sent to X as a question
 *   (`locationsQuery`);
 * - the tweet read by id takes at most `TWEET_IDS_MAX` ids in one request (the worker's
 *   own ceiling, read from X's field table), so the ids are deduplicated, the `null`
 *   ones left out, and the rest cut there with the cut counted (`tweetIdsToAsk`);
 * - a country's name comes from the list in hand or not at all: an id the list does
 *   not hold stays an id (`countryName`).
 */
import type {
	Campaign,
	PromotedTweet,
	TargetingCriterion,
	TargetingLocation,
	Tweet,
} from "./api.ts";

/**
 * The page size asked for the country list. X answered 242 countries to
 * `locationType=COUNTRIES&pageSize=250` on 2026-10-21 (the lead's read, curl); a page
 * this size holds the whole list that day, and `truncated` on the answer says if it
 * stops holding it.
 */
export const COUNTRIES_PAGE_SIZE = 250;

/** How many pages of line items the card follows before it stops and says so. */
export const MAX_PAGES = 10;

/**
 * The worker's ceiling on `ids=` for `GET /tweets` — X reads up to 200 in one request
 * (v1.ts `TWEET_IDS_MAX`, from the field table read 2026-10-07). Held here so the card
 * never sends a request the worker refuses by count.
 */
export const TWEET_IDS_MAX = 200;

/**
 * The query string for `GET /api/v1/targeting/locations`, with nothing empty on it.
 *
 * 🔴 `q` is left off when it is empty rather than sent as `q=`: the worker forwards
 * what it is given and defaults nothing (docs/API-CONTRACT.md, the locations route),
 * so an empty `q=` would reach X as a search for nothing. `pageSize` goes on the wire
 * only when asked for, for the same reason.
 */
export function locationsQuery(q: string, locationType?: string, pageSize?: number): string {
	const parts: string[] = [];
	if (q.trim() !== "") parts.push(`q=${encodeURIComponent(q.trim())}`);
	if (locationType !== undefined) parts.push(`locationType=${encodeURIComponent(locationType)}`);
	if (pageSize !== undefined) parts.push(`pageSize=${String(pageSize)}`);
	return parts.length === 0 ? "" : `?${parts.join("&")}`;
}

/** What following a cursor to its end — or to `maxPages` — leaves in hand. */
export interface AllPages<T> {
	rows: T[];
	/** True when the reading stopped at `maxPages` with a cursor still in hand. */
	moreNotRead: boolean;
	pagesRead: number;
}

/**
 * Every page a paged read answers, in order, until X answers no cursor — or until
 * `maxPages`, after which `moreNotRead` is true and the rows in hand are what the card
 * shows, labelled as such. One page at a time, each awaited, because the cursor for
 * the next page is on the one before.
 */
export async function allPages<T>(
	read: (cursor?: string) => Promise<{ rows: T[]; nextCursor: string | null }>,
	maxPages: number = MAX_PAGES,
): Promise<AllPages<T>> {
	const rows: T[] = [];
	let cursor: string | undefined;
	let pagesRead = 0;
	while (pagesRead < maxPages) {
		const page = await read(cursor);
		pagesRead += 1;
		rows.push(...page.rows);
		if (page.nextCursor === null) return { rows, moreNotRead: false, pagesRead };
		cursor = page.nextCursor;
	}
	return { rows, moreNotRead: true, pagesRead };
}

/** The one read by id the card sends for tweets, and what it had to leave out. */
export interface TweetAsk {
	/** Distinct ids, in the order first met, at most `TWEET_IDS_MAX`. */
	ids: string[];
	/** Distinct ids beyond the ceiling — not asked, and said so on their rows. */
	notAsked: string[];
	/** Promoted tweets whose `tweetId` is null: X sent the id as a number, so nothing can be asked. */
	unnamed: number;
}

/**
 * The tweet ids to read, from every promoted tweet on the card.
 *
 * 🔴 The ceiling is a cut and not a guess: ids past it are listed in `notAsked` so the
 * screen can say «not asked» on those rows rather than «X returned no tweet», which is
 * a different fact.
 */
export function tweetIdsToAsk(
	promoted: readonly PromotedTweet[],
	max: number = TWEET_IDS_MAX,
): TweetAsk {
	const seen = new Set<string>();
	const ids: string[] = [];
	const notAsked: string[] = [];
	let unnamed = 0;
	for (const p of promoted) {
		if (p.tweetId === null) {
			unnamed += 1;
			continue;
		}
		if (seen.has(p.tweetId)) continue;
		seen.add(p.tweetId);
		if (ids.length < max) ids.push(p.tweetId);
		else notAsked.push(p.tweetId);
	}
	return { ids, notAsked, unnamed };
}

/** X's rows by their `id_str`, so a promoted tweet's row finds its text in one lookup. */
export function tweetsById(tweets: readonly Tweet[]): Map<string, Tweet> {
	return new Map(tweets.map((t) => [t.id, t]));
}

/**
 * The name the country list in hand gives a criterion's id, or `null`.
 *
 * 🔴 Only a `LOCATION` criterion at `COUNTRIES` level is looked up, because that is the
 * only level the list was asked for; a region's or a city's id is not in it and must not
 * be matched against it by accident. `null` is the honest answer for everything else,
 * and the screen says the id and where the name is.
 */
export function countryName(
	c: Pick<TargetingCriterion, "targetingType" | "locationType" | "targetingValue">,
	countries: readonly TargetingLocation[] | null,
): string | null {
	if (countries === null) return null;
	if (c.targetingType !== "LOCATION" || c.locationType !== "COUNTRIES") return null;
	const row = countries.find((l) => l.targetingValue === c.targetingValue);
	return row ? row.name : null;
}

/**
 * Why a promoted tweet's row has no text, told apart by what was asked.
 *
 * - `no-id`: X sent the tweet id as a number, so there was nothing to ask for;
 * - `not-asked`: the id was past the ceiling of one request;
 * - `unread`: the request failed, or has not answered;
 * - `missing`: the request answered and X's rows do not include this id;
 * - `found`: X's row.
 */
export type TweetText =
	| { kind: "no-id" }
	| { kind: "not-asked" }
	| { kind: "unread" }
	| { kind: "missing" }
	| { kind: "found"; tweet: Tweet };

export function tweetText(
	tweetId: string | null,
	ask: TweetAsk,
	answer: ReadonlyMap<string, Tweet> | null,
): TweetText {
	if (tweetId === null) return { kind: "no-id" };
	if (ask.notAsked.includes(tweetId)) return { kind: "not-asked" };
	if (answer === null) return { kind: "unread" };
	const tweet = answer.get(tweetId);
	return tweet === undefined ? { kind: "missing" } : { kind: "found", tweet };
}

/**
 * The open card, as the campaign list holds it: which campaign, whether it was opened by
 * the row's Edit, and which opening this is. The page keys the card on
 * `${campaign.id}:${opened}`, so each opening is a new mount and starts from
 * `openingEdit` (campaign-manage.ts) — the card reads its edit state once, when it mounts.
 */
export interface CardOpen {
	campaign: Campaign;
	editing: boolean;
	opened: number;
}

/**
 * What a button on the campaign's row does to the open card (spec 2026-10-23).
 *
 * - `card` toggles: the same campaign's card closes, another campaign's opens reading.
 * - `edit` opens this campaign's card in its edit form, always as a new opening — so a
 *   card already open reading, or one whose form was saved and closed, opens the form
 *   again rather than staying as it was.
 *
 * 🔴 Without `writable` the row offers no Edit, and `edit` changes nothing: the row
 * hides controls, it grants nothing, and the card checks `writable` again on its side.
 */
export function cardFromRow(
	open: CardOpen | null,
	campaign: Campaign,
	how: "card" | "edit",
	writable: boolean,
): CardOpen | null {
	const opened = (open?.opened ?? 0) + 1;
	if (how === "edit") return writable ? { campaign, editing: true, opened } : open;
	return open?.campaign.id === campaign.id ? null : { campaign, editing: false, opened };
}
